import React, { useMemo } from "react";

import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useFormatNumericHook } from "hooks";
import { useNumberFormatSelector, useReportsQueriesSelector } from "store";
import { CashflowGraph, CurrencyType, ReportCashflowAum } from "store/types";
import { formatCurrency } from "utils/strings";

import { currencySign } from "../../../constants";

interface IProps {
  data: CashflowGraph | undefined;
  chartRef: React.RefObject<HighchartsReact.RefObject> | null;
  aumData: ReportCashflowAum | null;
  currency?: CurrencyType;
}

export const Chart: React.FC<IProps> = ({ data, chartRef, aumData, currency }) => {
  const { abbreviated, decimal } = useNumberFormatSelector();
  const formatNumeric = useFormatNumericHook();

  const { currency: currencyFromStore } = useReportsQueriesSelector();

  const curSymbol = useMemo(() => {
    return currencySign[currency || currencyFromStore];
  }, [currency, currencyFromStore]);

  const graphOptions: Highcharts.Options | undefined = useMemo(() => {
    if (data) {
      return {
        ...options,
        tooltip: {
          ...options.tooltip,
          formatter: function () {
            let tooltip = `<b>${this.x}</b><br/>`;
            if (this.points) {
              this.points.forEach((point) => {
                const value =
                  point.y !== null && point.y !== undefined
                    ? formatNumeric({ value: curSymbol + point.y, format: { abbreviated, decimal } })
                    : "N/A";
                tooltip += `${point.series.name}: <b>${value}</b><br/>`;
              });
            }
            return tooltip;
          }
        },
        yAxis: {
          ...options.yAxis,
          labels: {
            formatter: function () {
              const value = typeof this.value === "number" ? this.value : 0;
              return formatCurrency({ value, currencySymbol: curSymbol, abbreviated: true });
            }
          }
        },
        series: [
          {
            type: "column",
            name: "Unfunded",
            data: data.map((d) => d[0]),
            dataLabels: { enabled: false }
          },
          {
            type: "column",
            name: "Paid-in Capital",
            data: data.map((d) => d[2]),
            dataLabels: { enabled: false }
          },
          {
            type: "column",
            name: "Distribution",
            data: data.map((d) => d[3]),
            dataLabels: { enabled: false }
          },
          {
            type: "spline",
            name: "Valuation",
            data: data.map((d) => d[1]),
            marker: {
              symbol: "circle",
              fillColor: "#D9D9D9",
              lineWidth: 2,
              lineColor: "#26A6AE"
            },
            dataLabels: {
              enabled: !!aumData?.aum_enabled && !!aumData?.total_aum,
              backgroundColor: "#496682",
              y: -10,
              style: {
                color: "#ffffff",
                textOutline: "none",
                fontSize: "10px"
              },
              borderRadius: 4,
              padding: 4,
              allowOverlap: true,
              distance: 40,
              staggerLines: 2,
              overflow: "allow",
              crop: false,
              formatter: function () {
                return aumData?.aum_enabled && aumData?.total_aum && this.y
                  ? `${formatNumeric({ value: ((this.y / aumData.total_aum) * 100).toFixed(2), format: { abbreviated, decimal } })}%`
                  : "0%";
              }
            }
          },
          {
            type: "line" as const,
            name: "Allocation",
            data: data.map((d) => (d[4] ? d[4] : null)),
            dashStyle: "LongDash" as Highcharts.DashStyleValue,
            marker: {
              enabled: false
            }
          }
        ]
      };
    }
  }, [data, aumData, abbreviated, decimal, curSymbol, formatNumeric]);

  return (
    <HighchartsReact
      ref={chartRef}
      containerProps={{ style: { height: "100%" } }}
      highcharts={Highcharts}
      options={graphOptions}
    />
  );
};

const graphColors: string[] = [
  "#92A7BC", // Unfunded Commitment
  "#071F35", // Capital Call
  "#C09560", // Distribution
  "#26A6AE", // Valuation
  "#399FFD" // Allocation
];

const options: Highcharts.Options = {
  chart: {
    type: "container"
  },
  title: {
    text: undefined
  },
  exporting: { enabled: false },
  tooltip: {
    shared: true
  },
  legend: {
    itemStyle: {
      fontSize: "14px"
    }
  },
  xAxis: {
    title: { text: "Years" },
    plotBands: [
      {
        from: new Date().getFullYear() + 0.5,
        to: new Date().getFullYear() + 21,
        color: "rgba(249, 249, 249, 0.5)",
        zIndex: 7
      }
    ],
    plotLines: [
      {
        value: new Date().getFullYear() + 0.4,
        color: "#496682",
        width: 3,
        dashStyle: "LongDash"
      }
    ]
  },
  plotOptions: {
    series: {
      pointStart: new Date().getFullYear() - 5
    }
  },
  yAxis: {
    title: { text: "" },
    maxPadding: 0.2
  },
  series: [{ data: [], type: "column" }],
  responsive: {
    rules: [
      {
        condition: {
          maxWidth: 500
        },
        chartOptions: {
          legend: {
            layout: "horizontal",
            align: "center",
            verticalAlign: "bottom"
          }
        }
      }
    ]
  },
  colors: graphColors,
  credits: { enabled: false }
};
